// Project Properties
// -------------------

import { useState, useCallback, useEffect, FocusEvent } from "react";
import {
  APP_NAME_ALLOWED_CHARS_HINT,
  EMPTY_STRING,
  HTTP,
  HTTPS,
  MAX_LENGTH_APP_NAME,
  MAX_LENGTH_APP_URL,
} from "@/constants/definitions";
import WebApplicationClass from "@/models/webApplication";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import { getListOfAppNames, getListOfAppUrls } from "@/services/dataListOperations.service";
import { addProtocolIfMissing } from "@/utils/transform";
import { normalizeUrl } from "@/utils/normalizeUrl";
import { hasDisallowedUrlScheme } from "@/utils/urlSafety";
import { normalizeFreeText } from "@/utils/textSanitization";
import { APP_URL_WHITELIST_CHARS } from "@/utils/appUrlValidation";

const APP_URL_INPUT_NAME = "appUrl";
const APPNAME_INPUT_NAME = "appName";

const APP_NAME_WHITELIST_CHARS = /^[\sA-Za-z0-9-]+$/;
// APP_URL_WHITELIST_CHARS now lives in @/utils/appUrlValidation so the wizard
// and the Settings domain editor share one source of truth for the ruleset.

const ERROR_INVALID_CHARS_NAME = "Invalid characters in the name";
const ERROR_INVALID_CHARS_URL  = "Invalid URL";
const ERROR_HTTPS_REQUIRED     = "Only HTTPS URLs are accepted";
const ERROR_NAME_ALREADY_EXISTS = "Another web app with this name already exists";
const ERROR_URL_ALREADY_EXISTS  = "Another web app with this URL is already registered";
const APPNAME_MUST_BE_SET_ERROR = "The Application Name must be set";
const APPURL_MUST_BE_SET_ERROR  = "The Application URL must be set";

interface ProjectPropertiesProps {
  webapp: WebApplicationClass;
  newApp: boolean;
  returningToView: boolean;
  subscriptionClient: any;
  onComplete: (webapp: any) => void;
  // Optional — only the Add-Application wizard passes this. The
  // existing-app Settings flow also reuses this component but doesn't
  // need a Cancel affordance (it already lives inside a modal whose
  // close X handles the same intent).
  onCancel?: () => void;
}

function ProjectProperties({
  webapp,
  newApp,
  returningToView,
  onComplete,
  onCancel,
}: ProjectPropertiesProps) {
  const [appName, setAppName] = useState<string>(webapp.name);
  const [appUrl, setAppUrl] = useState<string>(webapp.url);
  const [errormessage, setErrormessage] = useState<string>(EMPTY_STRING);
  const [updateBackend, setUpdateBackend] = useState<boolean>(false);
  const [listOfAppNames, setListOfAppNames] = useState<string[]>([]);
  const [listOfAppUrls, setListOfAppUrls] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getListOfAppNames(), getListOfAppUrls()]).then(
      ([fetchedAppNames, fetchedAppUrls]) => {
        if (!cancelled) {
          setListOfAppNames(fetchedAppNames);
          setListOfAppUrls(fetchedAppUrls);
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    let shouldUpdateBackend = true;

    if (name === APP_URL_INPUT_NAME) {
      if (value.startsWith(HTTP) && !value.startsWith(HTTPS)) {
        shouldUpdateBackend = false;
      } else {
        const domain = value.startsWith(HTTPS) ? value.replace(HTTPS, "") : value;
        if (domain && !APP_URL_WHITELIST_CHARS.test(domain)) {
          shouldUpdateBackend = false;
        }
      }
    }
    if (name === APPNAME_INPUT_NAME) {
      if (value && !APP_NAME_WHITELIST_CHARS.test(value)) {
        shouldUpdateBackend = false;
      }
    }

    if (name === APP_URL_INPUT_NAME) {
      setAppUrl(value);
    } else if (name === APPNAME_INPUT_NAME) {
      setAppName(value);
    }
    setErrormessage(EMPTY_STRING); // clear any error while the user corrects their input
    setUpdateBackend(shouldUpdateBackend);
  }, []);

  const onAppURLInputBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    // Reject an explicit non-http(s) scheme (javascript:, data:, …) before
    // addProtocolIfMissing masks it by prepending https://. The backend is
    // authoritative (SECURITY_AUDIT_FINDINGS F3); this is the matching FE
    // guard so the user gets a clear message instead of a mangled value.
    const rawUrl = event.target.value;
    if (hasDisallowedUrlScheme(rawUrl)) {
      setAppUrl(rawUrl.trim());
      setErrormessage(ERROR_HTTPS_REQUIRED);
      setUpdateBackend(false);
      return;
    }
    // Add https:// if no protocol was entered; leave explicit http:// unchanged
    const normalizedUrl = addProtocolIfMissing(event.target.value);
    if (!normalizedUrl) {
      setAppUrl(normalizedUrl);
      return;
    }
    // Explicit http:// is not allowed
    if (normalizedUrl.startsWith(HTTP) && !normalizedUrl.startsWith(HTTPS)) {
      setAppUrl(normalizedUrl);
      setErrormessage(ERROR_HTTPS_REQUIRED);
      setUpdateBackend(false);
      return;
    }
    const domain = normalizedUrl.replace(HTTPS, "");
    if (!APP_URL_WHITELIST_CHARS.test(domain)) {
      setAppUrl(normalizedUrl);
      setErrormessage(ERROR_INVALID_CHARS_URL);
      setUpdateBackend(false);
      return;
    }

    if (newApp) {
      // Normalize both sides — the BE compares webapp URLs after the
      // canonical reduction in normalizeUrl, so a trailing slash /
      // default port / scheme-case difference on the user's input
      // would otherwise be missed here and rejected at create time.
      // Existing URLs from the list also pass through normalize so
      // legacy records (saved pre-normalizer) match correctly.
      const canonical = normalizeUrl(normalizedUrl);
      const duplicate = listOfAppUrls.some(
        (existingUrl) => normalizeUrl(existingUrl) === canonical,
      );
      if (duplicate) {
        setAppUrl(canonical);
        setErrormessage(ERROR_URL_ALREADY_EXISTS);
        setUpdateBackend(false);
        return;
      }
    }

    setAppUrl(normalizeUrl(normalizedUrl));
    setErrormessage(EMPTY_STRING);
    setUpdateBackend(true);
  }, [newApp, listOfAppUrls]);

  const onAppNameBlur = useCallback((): void => {
    if (appName && !APP_NAME_WHITELIST_CHARS.test(appName)) {
      setErrormessage(ERROR_INVALID_CHARS_NAME);
      return;
    }
    if (newApp && !returningToView) {
      if (listOfAppNames.indexOf(appName.toLowerCase()) > -1) {
        setErrormessage(ERROR_NAME_ALREADY_EXISTS);
        return;
      }
    }
    setErrormessage(EMPTY_STRING);
  }, [appName, newApp, returningToView, listOfAppNames]);

  const onButtonClick = useCallback(async (): Promise<void> => {
    let errorMsg = EMPTY_STRING;
    // Canonical form for both the duplicate check and the value we
    // hand off to the wizard. Re-derived here (not just taken from
    // appUrl state) so a user who clicks Next without blurring the
    // URL field still gets the same canonicalisation the blur handler
    // would have applied.
    let canonicalUrl = EMPTY_STRING;

    if (newApp) {
      if (!appName) {
        errorMsg = APPNAME_MUST_BE_SET_ERROR;
      } else if (!appUrl) {
        errorMsg = APPURL_MUST_BE_SET_ERROR;
      } else if (errormessage) {
        // Carry forward any existing validation error (URL format, http://, duplicate name)
        errorMsg = errormessage;
      } else if (hasDisallowedUrlScheme(appUrl)) {
        // Backstop the blur-time scheme check for the type-then-Next path
        // (SECURITY_AUDIT_FINDINGS F3) — reject javascript:/data:/… before
        // addProtocolIfMissing prepends https:// and hides the scheme.
        errorMsg = ERROR_HTTPS_REQUIRED;
      } else {
        // Guard against http:// if user clicked Next without blurring the URL field
        const withProtocol = addProtocolIfMissing(appUrl);
        if (withProtocol.startsWith(HTTP) && !withProtocol.startsWith(HTTPS)) {
          errorMsg = ERROR_HTTPS_REQUIRED;
        } else {
          // Backstop the duplicate-URL check that normally runs on
          // blur — catches the user who types and immediately clicks
          // Next. Same canonicalisation as the blur path so the two
          // can't disagree.
          canonicalUrl = normalizeUrl(withProtocol);
          const duplicate = listOfAppUrls.some(
            (existingUrl) => normalizeUrl(existingUrl) === canonicalUrl,
          );
          if (duplicate) {
            errorMsg = ERROR_URL_ALREADY_EXISTS;
          }
        }
      }
    }

    setErrormessage(errorMsg);

    if (errorMsg === EMPTY_STRING) {
      const webappCopy = webapp;
      if (newApp) {
        webappCopy.url = canonicalUrl || normalizeUrl(addProtocolIfMissing(appUrl));
        // Normalize the name (NFC, control-char strip, trim, length cap) so
        // the FE stores what the backend will store. The char whitelist is
        // enforced separately above. See SECURITY_AUDIT_FINDINGS F7.
        webappCopy.name = normalizeFreeText(appName, MAX_LENGTH_APP_NAME);
      }
      onComplete(webappCopy);
    }
  }, [newApp, appName, appUrl, errormessage, webapp, onComplete, listOfAppUrls]);

  return (
    <div className="flex flex-col gap-5">
      {errormessage && (
        <p className="text-sm text-red-600">{errormessage}</p>
      )}

      {/* Application Name */}
      {newApp ? (
        <FormField label="Application Name" htmlFor="appName">
          <Input
            type="text"
            name={APPNAME_INPUT_NAME}
            id="appName"
            value={appName}
            placeholder="e.g. Customer Portal"
            maxLength={MAX_LENGTH_APP_NAME}
            onChange={onInputChange}
            onBlur={onAppNameBlur}
          />
          <p className="mt-1 text-xs text-gray-400">{APP_NAME_ALLOWED_CHARS_HINT}</p>
        </FormField>
      ) : (
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
            Application Name
          </p>
          <p className="text-sm font-medium text-gray-900">{appName}</p>
        </div>
      )}

      {/* Application URL */}
      {newApp ? (
        <FormField label="Application URL" htmlFor="appUrl">
          <Input
            type="text"
            name={APP_URL_INPUT_NAME}
            id="appUrl"
            value={appUrl}
            placeholder="https://app.example.com"
            maxLength={MAX_LENGTH_APP_URL}
            onChange={onInputChange}
            onBlur={onAppURLInputBlur}
          />
        </FormField>
      ) : (
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
            Application URL
          </p>
          <p className="text-sm text-gray-500">{appUrl}</p>
        </div>
      )}

      {/* API domains are collected on the dedicated Pentest Scope step
          (wizard only) so there is deliberately no field for them here. */}

      <div className={`flex items-center pt-4 border-t border-gray-100 ${onCancel ? "justify-between" : "justify-end"}`}>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={onButtonClick}
          className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default ProjectProperties;
